import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { CertifiedBadge } from '../auth/LoginButton';
import { useComments, useCreateComment, useDeleteComment, useLikeComment } from '../../hooks/useComments';
import Spinner from '../common/Spinner';
import type { Comment } from '../../types/mjai';

interface CommentNodeProps {
  comment: Comment;
  handId: string;
  isReply: boolean;
  onReply: (parentId: string) => void;
  replyingTo: string;
  onAfterReply?: (rootId: string) => void; // 返信後に親ルートを展開するコールバック
  rootId: string; // この返信が属するルートコメントのID
}

function CommentNode({ comment, handId, isReply, onReply, replyingTo, onAfterReply, rootId }: CommentNodeProps) {
  const { user } = useAuth();
  const deleteComment = useDeleteComment(handId);
  const createComment = useCreateComment(handId);
  const likeComment = useLikeComment(handId);
  const [replyText, setReplyText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const isReplying = replyingTo === comment.id;

  const handleSubmitReply = async () => {
    if (!replyText.trim()) return;
    setIsSubmitting(true);
    try {
      await createComment.mutateAsync({
        content: replyText.trim(),
        parent_comment_id: comment.id,
      });
      setReplyText('');
      onReply('');
      onAfterReply?.(rootId);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('このコメントを削除しますか？')) return;
    await deleteComment.mutateAsync(comment.id);
  };

  const highlightMentions = (text: string) =>
    text.replace(/@([A-Za-z0-9_]+)/g, '<span class="text-blue-500 font-medium">@$1</span>');

  const displayName = comment.user ? comment.user.x_display_name : '匿名';
  const avatarUrl = comment.user ? comment.user.x_avatar_url : null;
  const isCertified = comment.user?.is_pro ?? false;
  const isOwn = user && comment.user && user.id === comment.user.id;

  return (
    <div className={isReply ? 'ml-6 border-l-2 border-gray-100 pl-3' : ''}>
      <div className="flex gap-2 py-2">
        {avatarUrl ? (
          <img src={avatarUrl} alt="" className="w-7 h-7 rounded-full shrink-0 mt-0.5" />
        ) : (
          <div className="w-7 h-7 rounded-full bg-gray-200 shrink-0 mt-0.5 flex items-center justify-center text-xs text-gray-500">
            {displayName[0]}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {comment.user ? (
              <Link
                to={`/users/${comment.user.x_username}`}
                className="text-xs font-semibold text-gray-800 hover:underline"
                onClick={(e) => e.stopPropagation()}
              >
                {displayName}
              </Link>
            ) : (
              <span className="text-xs font-semibold text-gray-800">{displayName}</span>
            )}
            {isCertified && <CertifiedBadge />}
            <span className="text-xs text-gray-400">
              {new Date(comment.created_at).toLocaleDateString('ja-JP', {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </span>
          </div>
          <p
            className="text-sm text-gray-700 mt-0.5 break-words"
            dangerouslySetInnerHTML={{ __html: highlightMentions(comment.content) }}
          />
          <div className="flex items-center gap-3 mt-1">
            <button
              onClick={() => {
                if (!user) return;
                likeComment.mutate({ commentId: comment.id, isLiked: comment.is_liked });
              }}
              disabled={likeComment.isPending}
              className={`flex items-center gap-1 text-xs transition-colors disabled:opacity-50
                ${comment.is_liked ? 'text-red-500' : 'text-gray-400 hover:text-red-400'}`}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24"
                fill={comment.is_liked ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth={1.8}>
                <path strokeLinecap="round" strokeLinejoin="round"
                  d="M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" />
              </svg>
              {comment.like_count > 0 && <span>{comment.like_count}</span>}
            </button>
            {user && (
              <button
                onClick={() => onReply(isReplying ? '' : comment.id)}
                className="text-xs text-gray-400 hover:text-blue-500"
              >
                {isReplying ? 'キャンセル' : '返信'}
              </button>
            )}
            {isOwn && (
              <button
                onClick={handleDelete}
                className="text-xs text-gray-400 hover:text-red-500"
              >
                削除
              </button>
            )}
          </div>

          {isReplying && user && (
            <div className="mt-2 flex gap-2">
              <textarea
                className="flex-1 border rounded p-2 text-sm resize-none"
                rows={2}
                placeholder={`@${comment.user?.x_username ?? ''} への返信`}
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                maxLength={500}
                autoFocus
              />
              <button
                onClick={handleSubmitReply}
                disabled={isSubmitting || !replyText.trim()}
                className="px-3 py-1 bg-green-600 text-white text-sm rounded disabled:opacity-50 self-end"
              >
                送信
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

interface CommentThreadProps {
  handId: string;
  inline?: boolean;
}

export default function CommentThread({ handId, inline = false }: CommentThreadProps) {
  const { user, login } = useAuth();
  const { data: comments, isLoading } = useComments(handId);
  const createComment = useCreateComment(handId);
  const [newContent, setNewContent] = useState('');
  const [replyingTo, setReplyingTo] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [expandedRoots, setExpandedRoots] = useState<Set<string>>(new Set());

  // ルートコメントと返信マップを構築（サーバーの順序を維持）
  const rootComments: Comment[] = [];
  const childMap = new Map<string, Comment[]>();
  for (const c of (comments ?? [])) {
    if (!c.parent_comment_id) {
      rootComments.push(c);
    } else {
      const arr = childMap.get(c.parent_comment_id) ?? [];
      arr.push(c);
      childMap.set(c.parent_comment_id, arr);
    }
  }

  // ルートコメントの全子孫をDFS順で取得
  function getDescendants(id: string): Comment[] {
    const children = childMap.get(id) ?? [];
    return children.flatMap((c) => [c, ...getDescendants(c.id)]);
  }

  const toggleReplies = (id: string) => {
    setExpandedRoots((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  // 返信送信後に対象ルートを自動展開
  const handleAfterReply = (rootId: string) => {
    setExpandedRoots((prev) => new Set(prev).add(rootId));
  };

  const handleSubmit = async () => {
    if (!newContent.trim()) return;
    setIsSubmitting(true);
    try {
      await createComment.mutateAsync({ content: newContent.trim() });
      setNewContent('');
    } finally {
      setIsSubmitting(false);
    }
  };

  const totalCount = comments?.length ?? 0;

  return (
    <div className={inline ? '' : 'border rounded-xl bg-white overflow-hidden'}>
      {!inline && (
        <div className="p-3 bg-gray-50 border-b">
          <h2 className="font-semibold text-gray-800">
            コメント
            {totalCount > 0 && (
              <span className="ml-2 text-sm font-normal text-gray-500">{totalCount}件</span>
            )}
          </h2>
        </div>
      )}

      <div className="p-4">
        {inline && totalCount > 0 && (
          <div className="flex items-center gap-2 mb-3">
            <span className="text-sm font-medium text-gray-600">コメント</span>
            <span className="text-xs text-gray-400">{totalCount}件</span>
          </div>
        )}

        {isLoading ? (
          <div className="flex justify-center py-4"><Spinner /></div>
        ) : rootComments.length === 0 && !user ? (
          <p className="text-sm text-gray-400 text-center py-4">まだコメントがありません</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {rootComments.map((root) => {
              const replies = getDescendants(root.id);
              const replyCount = replies.length;
              const isExpanded = expandedRoots.has(root.id);

              return (
                <div key={root.id}>
                  <CommentNode
                    comment={root}
                    handId={handId}
                    isReply={false}
                    onReply={setReplyingTo}
                    replyingTo={replyingTo}
                    onAfterReply={handleAfterReply}
                    rootId={root.id}
                  />

                  {replyCount > 0 && (
                    <button
                      onClick={() => toggleReplies(root.id)}
                      className="ml-9 mb-1 flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 font-medium"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" className={`w-3.5 h-3.5 transition-transform ${isExpanded ? 'rotate-180' : ''}`} viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                      {isExpanded ? '返信を非表示' : `${replyCount}件の返信`}
                    </button>
                  )}

                  {isExpanded && (
                    <div className="mb-1">
                      {replies.map((reply) => (
                        <CommentNode
                          key={reply.id}
                          comment={reply}
                          handId={handId}
                          isReply={true}
                          onReply={setReplyingTo}
                          replyingTo={replyingTo}
                          onAfterReply={handleAfterReply}
                          rootId={root.id}
                        />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}

        {user ? (
          <div className="mt-4 pt-3 border-t space-y-2">
            <textarea
              className="w-full border rounded p-2 text-sm resize-none"
              rows={3}
              placeholder="コメントを入力... @username でメンションできます"
              value={newContent}
              onChange={(e) => setNewContent(e.target.value)}
              maxLength={500}
            />
            <div className="flex justify-end">
              <button
                onClick={handleSubmit}
                disabled={isSubmitting || !newContent.trim()}
                className="px-4 py-1.5 bg-green-600 text-white text-sm rounded-lg disabled:opacity-50 font-medium"
              >
                {isSubmitting ? '送信中...' : 'コメントする'}
              </button>
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm text-gray-400 text-center">
            コメントするには
            <button onClick={login} className="text-blue-500 hover:underline mx-1">Xでログイン</button>
            が必要です
          </p>
        )}
      </div>
    </div>
  );
}
